'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import { HStack, VStack } from '@terra/ui-styled-system';
import { Button } from '../../../../../components/styled/button';
import { Text } from '../../../../../components/styled/text';
import { IconButton } from '../../../../../components/icon-button';
import { Field } from '../../../../../components/field';
import {
  Select,
  createSelectListCollection,
} from '../../../../../components/select';
import { Badge } from '../../../../../components/badge';
import {
  Undo02Icon,
  FloppyDiskIcon,
  Cancel01Icon,
  Target03Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Tooltip } from '../../../../../components/tooltip';
import {
  SignalCategorySchema,
  SignalStatus,
  type SignalStatusType,
} from '@terra/shared-schemas-orchestrator';
import { formatCategoryForDisplay } from '../../../../../utils/category-utils';
import { useChatEndpoints } from '../../../contexts/ChatEndpointsContext';
import { useResolvedSignalDetails } from '../../../hooks/useResolvedSignalDisplayTitle';
import { useFindingSearchContext } from '../../../finding-search';
import { MonoSmall } from '../../../../../components/typography';
import {
  buildUpdateSignalSubmitPayload,
  hasChanges,
  hasSubmitChanges,
  isFieldModified,
  getModifiedCount,
  omitEndpointUpdateFields,
  clearUpdateSignalEdits,
  type UpdateSignalData,
} from '../utils/update-signal-utils';
import { UpdateSignalAssetLinkingFields } from './UpdateSignalAssetLinkingFields';

export type { UpdateSignalData };
export type UpdateSignalEditFormProps = {
  onClose: () => void;
  onSubmit: (editedData: UpdateSignalData) => void;
  initialData: UpdateSignalData;
  headerActions?: React.ReactNode;
};
const categoryCollection = createSelectListCollection({
  items: SignalCategorySchema.options.map((category) => ({
    label: formatCategoryForDisplay(category),
    value: category,
  })),
});
const SIGNAL_STATUS_ORDER: readonly SignalStatusType[] = [
  SignalStatus.DISPROVEN_VULNERABILITY,
  SignalStatus.POTENTIAL_VULNERABILITY,
  SignalStatus.CONFIRMED_VULNERABILITY,
  SignalStatus.BLOCKED,
  SignalStatus.DUPLICATE,
];

export function UpdateSignalEditForm({
  onClose,
  onSubmit,
  initialData,
  headerActions,
}: UpdateSignalEditFormProps) {
  const t = useTranslations('designSystem.chat.updateSignalModal');
  const tEdit = useTranslations('designSystem.signals.editDrawer');
  const tSignalStatus = useTranslations(
    'designSystem.signals.lifecycle.status',
  );
  const { fetchEndpoints } = useChatEndpoints();
  const { onSearchAssets, onGetEndpointById, onGetHarEntriesByEndpoint } =
    useFindingSearchContext();
  const resolvedSignal = useResolvedSignalDetails({
    signalId: initialData.signalId,
    title: initialData.title,
  });
  const isEndpointEditable = resolvedSignal.isEndpointEditable === true;
  const endpointEditabilityKnown =
    resolvedSignal.isEndpointEditable !== undefined;
  const [endpointPreview, setEndpointPreview] = useState<{
    url?: string;
    method?: string;
  }>({});
  const [editedData, setEditedData] = useState<UpdateSignalData>(initialData);
  const baselineData = useMemo<UpdateSignalData>(
    () => ({
      signalId: initialData.signalId,
      status: resolvedSignal.status,
      category: resolvedSignal.category,
      endpointId: resolvedSignal.endpointId,
      harEntryId: resolvedSignal.harEntryId,
    }),
    [
      initialData.signalId,
      resolvedSignal.status,
      resolvedSignal.category,
      resolvedSignal.endpointId,
      resolvedSignal.harEntryId,
    ],
  );
  const effectiveEndpointId = isEndpointEditable
    ? (editedData.endpointId ?? resolvedSignal.endpointId)
    : resolvedSignal.endpointId;
  const statusCollection = useMemo(
    () =>
      createSelectListCollection({
        items: SIGNAL_STATUS_ORDER.map((status) => ({
          label: tSignalStatus(status),
          value: status,
        })),
      }),
    [tSignalStatus],
  );

  useEffect(() => {
    setEditedData({ ...initialData });
  }, [initialData]);
  useEffect(() => {
    if (!endpointEditabilityKnown || isEndpointEditable) {
      return;
    }

    setEditedData((prev) => omitEndpointUpdateFields(prev));
  }, [endpointEditabilityKnown, isEndpointEditable]);
  useEffect(() => {
    if (!isEndpointEditable) {
      setEndpointPreview({
        url: resolvedSignal.url,
        method: resolvedSignal.method,
      });

      return;
    }

    const endpointId = editedData.endpointId;

    if (!endpointId) {
      setEndpointPreview({});

      return;
    }

    if (resolvedSignal.endpointId === endpointId) {
      setEndpointPreview({
        url: resolvedSignal.url,
        method: resolvedSignal.method,
      });

      return;
    }

    let cancelled = false;
    const resolveProposedEndpoint = async () => {
      if (onGetEndpointById) {
        const endpoint = await onGetEndpointById(endpointId);

        if (cancelled) return;
        if (endpoint) {
          setEndpointPreview({
            url: endpoint.url,
            method: endpoint.method,
          });

          return;
        }
      }

      const endpoints = await fetchEndpoints();

      if (cancelled) return;
      const match = endpoints.find(
        (endpoint) => endpoint.endpointId === endpointId,
      );

      setEndpointPreview({
        url: match?.path || match?.endpointUrl,
        method: match?.method,
      });
    };

    resolveProposedEndpoint().catch((error: unknown) => {
      if (cancelled) return;
      console.error('Failed to resolve proposed endpoint preview', error);
      setEndpointPreview({});
    });

    return () => {
      cancelled = true;
    };
  }, [
    editedData.endpointId,
    fetchEndpoints,
    isEndpointEditable,
    onGetEndpointById,
    resolvedSignal.endpointId,
    resolvedSignal.method,
    resolvedSignal.url,
  ]);
  const currentStatusPlaceholder =
    resolvedSignal.status != null
      ? tSignalStatus(resolvedSignal.status)
      : resolvedSignal.status === null
        ? t('placeholders.unclassified')
        : tEdit('sections.signalInformation.fields.label.placeholder');
  const currentCategoryPlaceholder = resolvedSignal.category
    ? formatCategoryForDisplay(resolvedSignal.category)
    : tEdit('sections.signalInformation.fields.category.placeholder');
  const currentEndpointPlaceholder = resolvedSignal.url
    ? [resolvedSignal.method, resolvedSignal.url].filter(Boolean).join(' ')
    : t('typeToSearchEndpoints');
  const currentHarEntryPlaceholder = resolvedSignal.harEntryId;
  const handleEndpointChange = useCallback((endpointId: string | undefined) => {
    setEditedData((prev) => {
      const next = {
        ...prev,
        endpointId: endpointId || undefined,
      };

      if (endpointId !== prev.endpointId) {
        delete next.harEntryId;
      }

      return next;
    });
  }, []);
  const stagedEndpointPreview =
    isEndpointEditable && editedData.endpointId
      ? {
          url: endpointPreview.url,
          method: endpointPreview.method,
        }
      : isEndpointEditable
        ? undefined
        : {
            url: resolvedSignal.url,
            method: resolvedSignal.method,
          };
  const hasSignalChanges = hasChanges(editedData, baselineData);
  const canSubmit =
    endpointEditabilityKnown &&
    hasSubmitChanges({
      baselineData,
      editedData,
      isEndpointEditable,
    });
  const checkFieldModified = (field: keyof UpdateSignalData) =>
    isFieldModified(field, baselineData, editedData);
  const modifiedCount = getModifiedCount(baselineData, editedData);
  const handleFieldChange = useCallback(
    (
      field: keyof Omit<UpdateSignalData, 'signalId'>,
      value: string | undefined,
    ) => {
      setEditedData((prev) => ({ ...prev, [field]: value || undefined }));
    },
    [],
  );
  const handleResetField = useCallback(
    (field: keyof Omit<UpdateSignalData, 'signalId'>) => {
      setEditedData((prev) => {
        const next = { ...prev };

        delete next[field];

        return next;
      });
    },
    [],
  );
  const handleResetAll = useCallback(() => {
    setEditedData(clearUpdateSignalEdits(editedData));
  }, [editedData]);
  const handleSubmit = useCallback(() => {
    onSubmit(
      buildUpdateSignalSubmitPayload({
        baselineData,
        editedData,
        isEndpointEditable,
      }),
    );
    onClose();
  }, [baselineData, editedData, isEndpointEditable, onSubmit, onClose]);
  const handleClose = useCallback(() => {
    setEditedData({ ...initialData });
    onClose();
  }, [initialData, onClose]);
  const headerTitle = resolvedSignal.title
    ? `${tEdit('edit')}: ${resolvedSignal.title}`
    : t('title');
  const fieldResetControl = (
    field: keyof Omit<UpdateSignalData, 'signalId'>,
  ) =>
    checkFieldModified(field) ? (
      <HStack gap={1}>
        <Badge variant="emphasis" size="sm">
          {t('modified')}
        </Badge>
        <Tooltip title={t('resetToOriginal')}>
          <IconButton
            variant="ghost"
            size="xs"
            onClick={() => handleResetField(field)}
            aria-label={t('resetField')}
          >
            <Undo02Icon size={12} />
          </IconButton>
        </Tooltip>
      </HStack>
    ) : null;

  return (
    <VStack gap={4} padding={5} h="full" alignItems="stretch">
      <HStack
        justifyContent="space-between"
        alignItems="flex-start"
        width="100%"
      >
        <HStack gap={2} alignItems="flex-start" minWidth={0} flex={1}>
          <Target03Icon
            size={16}
            color="var(--colors-orange-9)"
            style={{ marginTop: 2, flexShrink: 0 }}
          />
          <VStack alignItems="flex-start" gap={0.5} minWidth={0}>
            <HStack gap={2} alignItems="center" minWidth={0}>
              <Text fontSize="sm" fontWeight="semibold" truncate>
                {headerTitle}
              </Text>
              {hasSignalChanges ? (
                <Badge variant="emphasis" size="sm" flexShrink={0}>
                  {t('fieldsModified', { count: modifiedCount })}
                </Badge>
              ) : null}
            </HStack>
            <Text fontSize="2xs" color="fg.muted">
              {tEdit('description')}
            </Text>
            <MonoSmall color="fg.muted" truncate maxWidth="100%">
              {editedData.signalId}
            </MonoSmall>
          </VStack>
        </HStack>
        <HStack gap={1} flexShrink={0}>
          {hasSignalChanges ? (
            <Tooltip title={t('resetAll')}>
              <IconButton
                variant="ghost"
                size="xs"
                onClick={handleResetAll}
                aria-label={t('resetAll')}
              >
                <Undo02Icon size={14} />
              </IconButton>
            </Tooltip>
          ) : null}
          {headerActions}
          <IconButton
            variant="ghost"
            size="xs"
            onClick={handleClose}
            aria-label={t('close')}
          >
            <Cancel01Icon size={14} />
          </IconButton>
        </HStack>
      </HStack>

      <VStack
        gap={3}
        p={4}
        bg="bg.default"
        borderRadius="md"
        borderWidth="1px"
        borderColor="border.default"
        w="full"
        alignItems="stretch"
      >
        <HStack w="full" justifyContent="space-between" alignItems="center">
          <Text fontSize="sm" fontWeight="semibold">
            {tEdit('sections.signalInformation.title')}
          </Text>
          <Text fontSize="2xs" color="fg.muted">
            {tEdit('sections.signalInformation.description')}
          </Text>
        </HStack>

        <HStack w="full" gap={3} alignItems="flex-start">
          <Field.Root flex={1}>
            <HStack justifyContent="space-between" alignItems="center">
              <Field.Label fontSize="xs" fontWeight="medium">
                {tEdit('sections.signalInformation.fields.label.label')}
              </Field.Label>
              {fieldResetControl('status')}
            </HStack>
            <Field.HelperText fontSize="2xs" color="fg.muted">
              {tEdit('sections.signalInformation.fields.label.helperText')}
            </Field.HelperText>
            <Select.Root
              positioning={{ sameWidth: true, strategy: 'fixed' }}
              size="sm"
              collection={statusCollection}
              value={editedData.status ? [editedData.status] : []}
              onValueChange={(e) =>
                handleFieldChange('status', e.value[0] as SignalStatusType)
              }
            >
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText placeholder={currentStatusPlaceholder} />
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content>
                  <Select.ItemGroup>
                    {statusCollection.items.map((item) => (
                      <Select.Item key={item.value} item={item}>
                        <Select.ItemText>{item.label}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.ItemGroup>
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </Field.Root>

          <Field.Root flex={1}>
            <HStack justifyContent="space-between" alignItems="center">
              <Field.Label fontSize="xs" fontWeight="medium">
                {tEdit('sections.signalInformation.fields.category.label')}
              </Field.Label>
              {fieldResetControl('category')}
            </HStack>
            <Field.HelperText fontSize="2xs" color="fg.muted">
              {tEdit('sections.signalInformation.fields.category.helperText')}
            </Field.HelperText>
            <Select.Root
              positioning={{ sameWidth: true, strategy: 'fixed' }}
              size="sm"
              collection={categoryCollection}
              value={editedData.category ? [editedData.category] : []}
              onValueChange={(e) => handleFieldChange('category', e.value[0])}
            >
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText placeholder={currentCategoryPlaceholder} />
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content>
                  <Select.ItemGroup>
                    {categoryCollection.items.map((item) => (
                      <Select.Item key={item.value} item={item}>
                        <Select.ItemText>{item.label}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.ItemGroup>
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </Field.Root>
        </HStack>

        <UpdateSignalAssetLinkingFields
          isEndpointEditable={isEndpointEditable}
          editedData={editedData}
          resolvedEndpointId={resolvedSignal.endpointId}
          resolvedHarEntryId={resolvedSignal.harEntryId}
          effectiveEndpointId={effectiveEndpointId}
          stagedEndpointPreview={stagedEndpointPreview}
          currentEndpointPlaceholder={currentEndpointPlaceholder}
          currentHarEntryPlaceholder={currentHarEntryPlaceholder}
          onEndpointChange={handleEndpointChange}
          onHarEntryChange={(harEntryId) =>
            handleFieldChange('harEntryId', harEntryId)
          }
          onSearchAssets={onSearchAssets}
          onGetHarEntriesByEndpoint={onGetHarEntriesByEndpoint}
          isFieldModified={checkFieldModified}
          onResetField={handleResetField}
        />
      </VStack>

      <HStack
        gap={2}
        justifyContent="space-between"
        width="100%"
        pt={2}
        borderTopWidth="1px"
        borderColor="border.default"
        marginTop="auto"
      >
        <Text fontSize="xs" color="fg.muted">
          {hasSignalChanges
            ? t('fieldsModifiedCount', { count: modifiedCount })
            : t('noChanges')}
        </Text>
        <HStack gap={2}>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            {tEdit('cancel')}
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            <FloppyDiskIcon size={14} />
            {t('submit')}
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );
}
